import { Spot } from '@/types/spots';
import { X, Plug, Wifi, Volume2, Users, Phone, Clock, Navigation, Instagram, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SpotDetailSheetProps {
  spot: Spot | null;
  onClose: () => void;
}

const RatingRow = ({ value, label, icon: Icon }: { value: number; label: string; icon: React.ElementType }) => {
  const labels = ['Limited', 'Fair', 'Good', 'Great', 'Excellent'];
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-gray-400" />
        <span className="text-gray-700">{label}</span>
      </div>
      <span className="text-gray-500 text-sm">{labels[value - 1]}</span>
    </div>
  );
};

const SpotDetailSheet = ({ spot, onClose }: SpotDetailSheetProps) => {
  if (!spot) return null;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(spot.address)}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/30 z-50 tablet:hidden"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[80vh] overflow-y-auto tablet:hidden"
      >
        {/* Handle */}
        <div className="sticky top-0 bg-white pt-3 pb-4 px-6">
          <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-6 pb-10">
          {/* Image */}
          {spot.imageUrl && (
            <div className="rounded-xl overflow-hidden mb-4 -mx-2">
              <img
                src={spot.imageUrl}
                alt={spot.name}
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {/* Header */}
          <h2 className="text-2xl font-bold text-[#374151] mb-1">{spot.name}</h2>
          <p className="text-gray-400 mb-4">{spot.type}</p>

          {/* Social Links */}
          <div className="flex gap-2 mb-6">
            {spot.instagramUrl && (
              <a
                href={spot.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-600 transition-colors"
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </a>
            )}
            {spot.websiteUrl && (
              <a
                href={spot.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-600 transition-colors"
              >
                <Globe className="w-4 h-4" />
                Website
              </a>
            )}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {spot.callFriendly && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                <Phone className="w-4 h-4" />
                Call-friendly
              </span>
            )}
            {spot.openingHours && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
                <Clock className="w-4 h-4" />
                {spot.openingHours}
              </span>
            )}
          </div>

          {/* Ratings */}
          <div className="mb-8">
            <RatingRow value={spot.powerOutlets} label="Power outlets" icon={Plug} />
            <RatingRow value={spot.wifiQuality} label="WiFi quality" icon={Wifi} />
            <RatingRow value={spot.quietLevel} label="Noise level" icon={Volume2} />
            <RatingRow value={spot.seatAvailability} label="Seat availability" icon={Users} />
          </div>

          {/* Address */}
          <p className="text-gray-500 text-sm mb-6">{spot.address}</p>

          {/* CTA */}
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-4 bg-[#1f3d32] text-white rounded-full font-medium text-lg hover:bg-[#1f3d32]/90 transition-colors"
          >
            <Navigation className="w-5 h-5" />
            Get directions
          </a>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SpotDetailSheet;
